/* eslint-disable react/no-array-index-key */
import React, { useRef, useEffect, useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { SvgShapeDown, SvgShapeUp } from "@routes/IconeSvg";
import moment from "moment-timezone";
import { Controller } from "react-hook-form";

import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import FormTextarea from "@components/formGroup/formTextarea";
import InputWithAddOptions from "@components/input/InputWithAddOptions";
import {
  Input,
  Switch,
  DropdownDevise,
  InputGroup
} from "@components/tailwindComponents";
import ListboxSimple from "@components/tailwindComponents/ListboxSimple";
import useAdvantageType from "@pages/shared/employeFormNew/JobAndSalary/SharedCourseAndSalary/hooks/useAdvantageType";
import { JOBTYPE_LIST } from "@pages/shared/employeFormNew/employeForm-constants";

import { formatToNumber } from "@helpers/_functions";

const DATE_FORMAT = "YYYY-MM-DD";

export const useSectionCourseAndSalaryColumns = (maxAdvantageTypes = 0) => {
  const { t } = useTranslation("pages.employés.emploi&Salary");

  return useMemo(() => {
    const columns = [
      { id: "title", label: t("Titre de parcours") + "*" },
      { id: "emploiCourses", label: t("Poste") + "*" },
      { id: "salaryBegin", label: t("Parcours (début)") },
      { id: "salaryEnd", label: t("Parcours (fin)") },
      { id: "isInProgress", label: t("En cours") },
      { id: "jobType", label: t("Type d'emploi") + "*" },
      { id: "salary", label: t("Salaire annuel") + "*" },
      { id: "hourlyRate", label: t("Taux horaire") },
      { id: "hoursPerWeek", label: t("Heures par semaine") },
      { id: "vacancyPercentage", label: t("Pourcentage de vacance") + "*" },
      { id: "additionalInformation", label: t("Notes") }
    ];

    Array(maxAdvantageTypes)
      .fill()
      .forEach((_, typeIndex) => {
        columns.push(
          { id: `advantageType-${typeIndex}`, label: `${t("Type d'avantage")} ${typeIndex + 1}` },
          { id: `advantageValue-${typeIndex}`, label: `${t("Valeur annuelle")} ${typeIndex + 1}` },
          { id: `advantageNote-${typeIndex}`, label: `${t("Notes")} ${typeIndex + 1}` }
        );
      });

    return columns;
  }, [t, maxAdvantageTypes]);
};

export const SectionCourseAndSalaryTableBulkHeader = ({
  maxAdvantageTypes,
  hiddenColumns = {}
}) => {
  const { t: mainTranslator } = useTranslation("pages.employés.emploi&Salary");

  return (
    <>
      <th className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.title ? "arh-hidden" : ""}`}>
        {mainTranslator("Titre de parcours") + "*"}
      </th>
      <th className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.emploiCourses ? "arh-hidden" : ""}`}>
        {mainTranslator("Poste") + "*"}
      </th>
      <th className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.salaryBegin ? "arh-hidden" : ""}`}>
        {mainTranslator("Parcours (début)")}
      </th>
      <th className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.salaryEnd ? "arh-hidden" : ""}`}>
        {mainTranslator("Parcours (fin)")}
      </th>
      <th className={`arh-w-[8rem] arh-min-w-[8rem] arh-max-w-[8rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-center arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.isInProgress ? "arh-hidden" : ""}`}>
        {mainTranslator("En cours")}
      </th>
      <th className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.jobType ? "arh-hidden" : ""}`}>
        {mainTranslator("Type d'emploi") + "*"}
      </th>
      <th className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.salary ? "arh-hidden" : ""}`}>
        {mainTranslator("Salaire annuel") + "*"}
      </th>
      <th className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.hourlyRate ? "arh-hidden" : ""}`}>
        {mainTranslator("Taux horaire")}
      </th>
      <th className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.hoursPerWeek ? "arh-hidden" : ""}`}>
        {mainTranslator("Heures par semaine")}
      </th>
      <th className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.vacancyPercentage ? "arh-hidden" : ""}`}>
        {mainTranslator("Pourcentage de vacance") + "*"}
      </th>
      <th className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.additionalInformation ? "arh-hidden" : ""}`}>
        {mainTranslator("Notes")}
      </th>
      {Array(maxAdvantageTypes)
        .fill()
        .map((_, typeIndex) => (
          <React.Fragment key={`advantage-type-header-${typeIndex}`}>
            <th className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns[`advantageType-${typeIndex}`] ? "arh-hidden" : ""}`}>
              {mainTranslator("Type d'avantage")}
            </th>
            <th className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns[`advantageValue-${typeIndex}`] ? "arh-hidden" : ""}`}>
              {mainTranslator("Valeur annuelle")}
            </th>
            <th className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns[`advantageNote-${typeIndex}`] ? "arh-hidden" : ""}`}>
              {mainTranslator("Notes")}
            </th>
          </React.Fragment>
        ))}
    </>
  );
};

export const SectionCourseAndSalaryTableBulkCells = ({
  control,
  mustWait,
  readOnly,
  employeeIndex,
  employee,
  careerList,
  getValues,
  setValue,
  watch,
  maxAdvantageTypes,
  hiddenColumns = {}
}) => {
  const { t: mainTranslator } = useTranslation("pages.employés.emploi&Salary");
  const salaryAndAdvantages = employee?.salaryAndAdvantages || [];
  const {
    advantageTypes: advantageTypesList,
    addAdvantageType,
    updateAdvantageType,
    deleteAdvantageType
  } = useAdvantageType();

  const makeRequiredRule = useCallback(() => {
    return {
      validate: {
        required: (value) => {
          const isSelected = getValues(`employees.${employeeIndex}.selected`);
          if (!isSelected) return true;
          return !!value;
        }
      }
    };
  }, [getValues, employeeIndex]);

  function onUpClicked(value, onChange) {
    value = Number.parseInt(value) || 0;
    onChange(value + 1);
  }

  function onDownClicked(value, onChange) {
    value = Number.parseInt(value) || 0;
    const nextValue = value > 0 ? value - 1 : 0;
    onChange(nextValue);
  }

  return salaryAndAdvantages?.map((advantage, advantageIndex) => {
    const careerRef = useRef(null);
    const emploiCourse = advantage?.emploiCourses;

    useEffect(() => {
      if (emploiCourse?._id && !careerRef.current) {
        const actualCourse = { ...emploiCourse };
        if (!actualCourse.value) {
          actualCourse.value = actualCourse._id;
        }
        careerRef.current = actualCourse;
      }
    }, [emploiCourse?._id]);

    useEffect(() => {
      const currentCourse = watch(
        `employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.emploiCourses`
      );
      if (
        (emploiCourse?._id || currentCourse?._id) &&
        careerRef.current?._id !== (emploiCourse?._id || currentCourse?._id)
      ) {
        const actualCourse = emploiCourse?._id ? emploiCourse : currentCourse;
        if (actualCourse && !actualCourse.value) {
          actualCourse.value = actualCourse._id;
        }
        careerRef.current = actualCourse;
      }
    }, [emploiCourse, employeeIndex, advantageIndex, careerRef.current]);

    useEffect(() => {
      if (careerRef.current?.status) {
        setValue(
          `employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.salaryBegin`,
          moment(careerRef.current.dateStart, DATE_FORMAT).toDate()
        );
      }
    }, [careerRef.current]);

    const advantageTypes = advantage.AdvantageType || [];

    const [isInProgress] = watch([
      `employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.isInProgress`
    ]);

    return (
      <React.Fragment key={`advantage-${advantage?._id}`}>
        <td className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.title ? "arh-hidden" : ""}`}>
          <Controller
            control={control}
            name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}._id`}
            defaultValue={advantage._id || null}
            render={({ field }) => <input {...field} type="hidden" />}
          />
          <Controller
            control={control}
            name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.emploiCourses`}
            defaultValue={emploiCourse}
            render={({ field }) => <input {...field} type="hidden" />}
          />
          <Controller
            control={control}
            name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.title`}
            defaultValue={advantage.title}
            rules={makeRequiredRule()}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid }
            }) => (
              <Input
                name={name}
                onChange={onChange}
                value={value}
                invalid={invalid}
                disabled={mustWait || readOnly}
                placeholder={mainTranslator("Titre de parcours") + "*"}
                additionnalClas="!arh-py-[5px] !arh-px-2.5"
              />
            )}
          />
        </td>

        <td className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.emploiCourses ? "arh-hidden" : ""}`}>
          <Controller
            control={control}
            name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.emploiCourses._id`}
            defaultValue={emploiCourse._id || ""}
            rules={makeRequiredRule()}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid }
            }) => {
              const selectedCareer = careerList.find(
                (career) => career._id === value
              );
              return (
                <div className="blackSvgColor position_relative">
                  <ListboxSimple
                    name={name}
                    placeholder={mainTranslator("Nom du poste")}
                    optionList={careerList}
                    optionTextAttribut="title"
                    selectedValue={selectedCareer}
                    setSelectedValue={(val) => onChange(val?._id || "")}
                    invalid={invalid}
                    noRadio={true}
                    withSearch
                    showReset={!emploiCourse?._id && !readOnly}
                    readOnly={mustWait || emploiCourse?._id || readOnly}
                    classParentList="arh-mb-0"
                    customButtonClass="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-whitespace-nowrap arh-overflow-hidden"
                    searchInputClass="!arh-py-[5px] !arh-px-2.5"
                    customTranslator={mainTranslator}
                  />
                </div>
              );
            }}
          />
        </td>

        <td className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.salaryBegin ? "arh-hidden" : ""}`}>
          <div className="flex-column calendar_with_btn employerThemeDatePicker wadeDatePicker arh-flex arh-w-full">
            {mustWait || readOnly ? (
              <Input
                value={advantage.salaryBegin}
                placeholder={mainTranslator("00/00/00")}
                disabled
                additionnalClas="!arh-py-[5px] !arh-px-2.5"
              />
            ) : (
              <Controller
                control={control}
                name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.salaryBegin`}
                defaultValue={advantage.salaryBegin}
                rules={makeRequiredRule()}
                render={({
                  field: { onChange, value },
                  fieldState: { invalid }
                }) => (
                  <DefaultDatePicker
                    placeholder={mainTranslator("00/00/00")}
                    dateValue={value}
                    setDateValue={onChange}
                    className="focus_green dateOfBirth"
                    inputDatePicker="form-control !arh-h-[calc(2rem+2px)] !arh-py-[5px] !arh-px-2.5"
                    invalid={invalid}
                    readOnly={
                      mustWait ||
                      readOnly ||
                      Boolean(careerRef.current?.status)
                    }
                  />
                )}
              />
            )}
          </div>
        </td>

        <td className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.salaryEnd ? "arh-hidden" : ""}`}>
          <div className="flex-column calendar_with_btn employerThemeDatePicker wadeDatePicker arh-flex arh-w-full">
            {mustWait || readOnly ? (
              <Input
                value={advantage.salaryEnd}
                placeholder={mainTranslator("00/00/00")}
                disabled
                additionnalClas="!arh-py-[5px] !arh-px-2.5"
              />
            ) : (
              <Controller
                control={control}
                name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.salaryEnd`}
                defaultValue={advantage.salaryEnd}
                rules={makeRequiredRule()}
                render={({
                  field: { onChange, value },
                  fieldState: { invalid }
                }) => (
                  <DefaultDatePicker
                    placeholder={mainTranslator("00/00/00")}
                    dateValue={value}
                    setDateValue={onChange}
                    className="focus_green dateOfBirth"
                    inputDatePicker="form-control !arh-h-[calc(2rem+2px)] !arh-py-[5px] !arh-px-2.5"
                    invalid={invalid}
                    readOnly={
                      mustWait ||
                      readOnly ||
                      isInProgress ||
                      Boolean(careerRef.current?.status)
                    }
                  />
                )}
              />
            )}
          </div>
        </td>

        <td className={`arh-w-[8rem] arh-min-w-[8rem] arh-max-w-[8rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.isInProgress ? "arh-hidden" : ""}`}>
          <div className="arh-flex arh-h-full arh-items-center arh-justify-center">
            {!readOnly && (
              <Controller
                control={control}
                name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.isInProgress`}
                defaultValue={advantage.isInProgress}
                render={({ field: { onChange, value } }) => (
                  <Switch
                    color={mustWait ? "" : "arh-bg-greenColor"}
                    checked={value}
                    handleChange={() => onChange(!value)}
                    disabled={mustWait || Boolean(careerRef.current?.status)}
                  />
                )}
              />
            )}
          </div>
        </td>

        <td className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.jobType ? "arh-hidden" : ""}`}>
          <Controller
            control={control}
            name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.jobType`}
            defaultValue={advantage.jobType || ""}
            render={({ field: { onChange, value, name } }) => {
              const selectedJobType = JOBTYPE_LIST?.map((jobtype) => ({ ...jobtype, name: mainTranslator(jobtype.name) }))?.find(
                (job) => job.value === value
              );
              return (
                <ListboxSimple
                  name={name}
                  placeholder={mainTranslator("Type d'emploi")}
                  optionList={JOBTYPE_LIST.sort((a, b) => a.name.localeCompare(b.name))}
                  selectedValue={selectedJobType}
                  setSelectedValue={(val) => onChange(val?.value || "")}
                  readOnly={mustWait || readOnly}
                  classParentList="arh-mb-0"
                  customButtonClass="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-whitespace-nowrap arh-overflow-hidden"
                  searchInputClass="!arh-py-[5px] !arh-px-2.5"
                  customTranslator={mainTranslator}
                  noRadio={true}
                  withSearch
                />
              );
            }}
          />
        </td>

        <td className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.salary ? "arh-hidden" : ""}`}>
          <div className="arh-relative arh-flex arh-w-full">
            <Controller
              control={control}
              name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.salary`}
              defaultValue={advantage.salary}
              render={({ field: { onChange, name, value } }) => (
                <InputGroup
                  name={name}
                  onChange={(event) => {
                    const nextValue = formatToNumber(event.target.value);
                    onChange(nextValue);
                  }}
                  value={value}
                  disabled={mustWait || readOnly}
                  placeholder={mainTranslator("Salaire")}
                  classParent="arh-w-full"
                  classContent="arh-w-[4.25rem] !arh-h-auto"
                  classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
                  content={
                    <DropdownDevise
                      control={control}
                      name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.devise`}
                      defaultValue={{
                        _id: advantage.devise,
                        name: advantage.devise
                      }}
                      disable={mustWait || readOnly}
                      customButtonClass="!arh-pb-[5px] !arh-pt-1.5 !arh-px-2.5 !arh-border-0 !arh-bg-transparent"
                    />
                  }
                />
              )}
            />
          </div>
        </td>

        <td className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.hourlyRate ? "arh-hidden" : ""}`}>
          <div className="arh-relative arh-flex arh-w-full">
            <Controller
              control={control}
              name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.hourlyRate`}
              defaultValue={advantage.hourlyRate}
              render={({ field: { onChange, name, value } }) => (
                <InputGroup
                  name={name}
                  onChange={(event) => {
                    const nextValue = formatToNumber(event.target.value);
                    onChange(nextValue);
                  }}
                  value={value}
                  disabled={mustWait || readOnly}
                  placeholder={mainTranslator("Taux horaire")}
                  classParent="arh-w-full"
                  classContent="arh-w-[4.25rem] !arh-h-auto"
                  classInput="!arh-py-[5px] !arh-px-2.5 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
                  content={
                    <DropdownDevise
                      control={control}
                      name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.hourlyRateDevise`}
                      defaultValue={{
                        _id: advantage.hourlyRateDevise,
                        name: advantage.hourlyRateDevise
                      }}
                      disable={mustWait || readOnly}
                      customButtonClass="!arh-pb-[5px] !arh-pt-1.5 !arh-px-2.5 !arh-border-0 !arh-bg-transparent"
                    />
                  }
                />
              )}
            />
          </div>
        </td>

        <td className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.hoursPerWeek ? "arh-hidden" : ""}`}>
          <Controller
            control={control}
            name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.hoursPerWeek`}
            defaultValue={advantage.hoursPerWeek}
            render={({ field: { onChange, value, name } }) => (
              <div className="blackSvgColor">
                <InputGroup
                  name={name}
                  value={value}
                  onChange={onChange}
                  disabled={mustWait || readOnly}
                  placeholder={mainTranslator("Heures par semaine")}
                  classParent="arh-w-full"
                  classContent="arh-w-11 !arh-h-8"
                  classInput="!arh-py-[5px] !arh-px-2.5 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
                  content={
                    <div className="!arh-px-2.5 !arh-py-1">
                      <div
                        onClick={() => {
                          if (!readOnly) {
                            onUpClicked(value, onChange);
                          }
                        }}
                        className="arh-cursor-pointer"
                      >
                        {SvgShapeUp()}
                      </div>
                      <div
                        onClick={() => {
                          if (!readOnly) {
                            onDownClicked(value, onChange);
                          }
                        }}
                        className="arh-cursor-pointer"
                      >
                        {SvgShapeDown()}
                      </div>
                    </div>
                  }
                />
              </div>
            )}
          />
        </td>

        <td className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-truncate arh-whitespace-nowrap arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.vacancyPercentage ? "arh-hidden" : ""}`}>
          <Controller
            control={control}
            name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.vacancyPercentage`}
            defaultValue={advantage.vacancyPercentage}
            render={({ field: { onChange, value, name } }) => (
              <InputGroup
                name={name}
                onChange={onChange}
                value={value}
                disabled={mustWait || readOnly}
                placeholder={mainTranslator("Pourcentage de vacance")}
                classParent="arh-w-full"
                classContent="arh-w-11 !arh-h-auto"
                classInput="!arh-py-[5px] !arh-px-2.5 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
                content={
                  <div className="!arh-px-2.5 !arh-pb-[4.5px] !arh-pt-1.5">
                    %
                  </div>
                }
              />
            )}
          />
        </td>

        <td className={`arh-w-[13.375rem] arh-min-w-[13.375rem] arh-max-w-[13.375rem] arh-whitespace-nowrap arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.additionalInformation ? "arh-hidden" : ""}`}>
          <Controller
            control={control}
            name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.additionalInformation`}
            defaultValue={advantage.additionalInformation}
            render={({ field: { onChange, value, name } }) => (
              <FormTextarea
                placeholder={mainTranslator("Notes")}
                classTextarea="!arh-py-[5px] !arh-px-2.5 arh-h-[54px]"
                name={name}
                value={value}
                onChange={onChange}
                readOnly={mustWait || readOnly}
              />
            )}
          />
        </td>

        {Array(maxAdvantageTypes)
          .fill()
          .map((_, typeIndex) => (
            <AdvantageTypeItems
              key={`advantage-type-${employeeIndex}-${advantageIndex}-${typeIndex}`}
              control={control}
              readOnly={mustWait || readOnly}
              employeeIndex={employeeIndex}
              advantageIndex={advantageIndex}
              typeIndex={typeIndex}
              advantageType={advantageTypes[typeIndex]}
              mainTranslator={mainTranslator}
              advantageTypesList={advantageTypesList}
              addAdvantageType={addAdvantageType}
              updateAdvantageType={updateAdvantageType}
              deleteAdvantageType={deleteAdvantageType}
              getValues={getValues}
              hiddenColumns={hiddenColumns}
            />
          ))}
      </React.Fragment>
    );
  });
};

const AdvantageTypeItems = ({
  control,
  readOnly,
  employeeIndex,
  advantageIndex,
  typeIndex,
  advantageType,
  mainTranslator,
  advantageTypesList,
  addAdvantageType,
  updateAdvantageType,
  deleteAdvantageType,
  getValues,
  hiddenColumns = {}
}) => {
  const makeAdvantageTypeRule = useCallback(() => {
    return {
      validate: {
        required: (value) => {
          const isSelected = getValues(`employees.${employeeIndex}.selected`);
          if (!isSelected) return true;
          return !!value?.name;
        }
      }
    };
  }, [getValues, employeeIndex]);

  if (!advantageType) {
    return (
      <React.Fragment
        key={`advantage-type-${employeeIndex}-${advantageIndex}-${typeIndex}`}
      >
        <td className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-whitespace-nowrap arh-bg-greyColor arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[`advantageType-${typeIndex}`] ? "arh-hidden" : ""}`} />
        <td className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[`advantageValue-${typeIndex}`] ? "arh-hidden" : ""}`} />
        <td className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-whitespace-nowrap arh-bg-greyColor arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[`advantageNote-${typeIndex}`] ? "arh-hidden" : ""}`} />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment
      key={`advantage-type-${employeeIndex}-${advantageIndex}-${typeIndex}`}
    >
      <td className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-whitespace-nowrap arh-bg-greyColor arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[`advantageType-${typeIndex}`] ? "arh-hidden" : ""}`}>
        <Controller
          control={control}
          name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.AdvantageType.${typeIndex}`}
          defaultValue={advantageType}
          rules={makeAdvantageTypeRule()}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid }
          }) => (
            <InputWithAddOptions
              name={name}
              value={
                advantageTypesList.find((item) => item?._id === value?._id)
                  ?.name || value?.name
              }
              onChange={(event) => {
                const newData = event?.target?.dataset;
                if (newData) {
                  onChange({
                    ...value,
                    _id: newData.id || "",
                    name: newData.value || ""
                  });
                }
              }}
              disabled={readOnly}
              defaultText={mainTranslator("Type d'avantage")}
              defaultSelected={value?._id}
              optionsList={advantageTypesList}
              secondDefaultText={mainTranslator("Ajouter")}
              optionListLabel="name"
              addItem={addAdvantageType}
              editItem={updateAdvantageType}
              deleteItem={deleteAdvantageType}
              validateDropdown={invalid}
              showAction
              classParentList="arh-mb-0"
              classBtn="arh-absolute arh-right-1 arh-top-1/2 -arh-translate-y-1/2 !arh-py-[0.0625rem] !arh-px-2.5"
              classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-whitespace-nowrap arh-overflow-hidden"
            />
          )}
        />
      </td>

      <td className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-bg-greyColor arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[`advantageValue-${typeIndex}`] ? "arh-hidden" : ""}`}>
        <div className="arh-relative arh-flex arh-w-full">
          <Controller
            control={control}
            name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.AdvantageType.${typeIndex}.annualValue`}
            defaultValue={advantageType.annualValue}
            render={({ field: { onChange, name, value } }) => (
              <InputGroup
                name={name}
                onChange={(event) => {
                  const nextValue = formatToNumber(event.target.value);
                  onChange(nextValue);
                }}
                value={value}
                disabled={readOnly}
                placeholder={mainTranslator("Valeur")}
                classParent="arh-w-full"
                classContent="arh-w-[4.25rem] !arh-h-auto"
                classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
                content={
                  <DropdownDevise
                    control={control}
                    name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.AdvantageType.${typeIndex}.annualDevise`}
                    defaultValue={advantageType.annualDevise}
                    disable={readOnly}
                    customButtonClass="!arh-pb-[5px] !arh-pt-1.5 !arh-px-2.5 !arh-border-0 !arh-bg-transparent"
                  />
                }
              />
            )}
          />
        </div>
      </td>

      <td className={`arh-w-[15.625rem] arh-min-w-[15.625rem] arh-max-w-[15.625rem] arh-whitespace-nowrap arh-bg-greyColor arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[`advantageNote-${typeIndex}`] ? "arh-hidden" : ""}`}>
        <Controller
          control={control}
          name={`employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.AdvantageType.${typeIndex}.note`}
          defaultValue={advantageType.note}
          render={({ field: { onChange, value, name } }) => (
            <FormTextarea
              placeholder={mainTranslator("Notes")}
              classTextarea="!arh-py-[5px] !arh-px-2.5 arh-h-[54px]"
              name={name}
              value={value}
              onChange={onChange}
              readOnly={readOnly}
            />
          )}
        />
      </td>
    </React.Fragment>
  );
};

export const getCourseAndSalaryColumnCount = (maxAdvantageTypes = 0) => {
  const baseColumns = 11;
  const advantageTypeColumns = 3;
  return baseColumns + advantageTypeColumns * maxAdvantageTypes;
};
